// @flow
import React, { useContext } from 'react';
import injectSheet from 'react-jss';
import classnames from 'classnames';
import EditorContext from 'shared/components/EditorContext';
import type { ComponentEditor, SubcomponentEditor } from 'shared/components/EditorContext';
import { zIndex } from 'shared/constants';
import ErrorBoundary from 'shared/components/Error/ErrorBoundary';

declare var confirm: (string) => {};

type Props = ComponentEditor & {
  component: $FlowTODO,
  widgetName: string,
  componentProps: Object,
  classes: Object,
  isUsingPresetId: boolean,
  showLeftRight: boolean,
  isSubcomponent: boolean,
  onDragStart: Function,
};

function SlotControls({
  component: Component,
  widgetName,
  componentProps,
  classes,
  isUsingPresetId,
  isSubcomponent,
  showLeftRight,
  onClickAdd: _onClickAdd,
  onClickEdit: _onClickEdit,
  onClickPresets,
  onClickDelete: _onClickDelete,
  onClickMove: _onClickMove,
  onClickCopy: _onClickCopy,
  onDragStart,
}: Props) {
  // Configure functions for controls in subcomponents
  const onClickEdit = () => _onClickEdit(isSubcomponent ? componentProps._id : null);
  const onClickDelete = () => _onClickDelete(isSubcomponent ? componentProps._id : null);
  const onClickMove = direction => _onClickMove(direction, isSubcomponent ? componentProps._id : null);
  const onClickCopy = () => _onClickCopy(isSubcomponent ? componentProps._id : null);

  const confirmDelete = (event) => {
    if (confirm(`Are you sure you want to delete the ${widgetName} component?`)) {
      onClickDelete(event);
    }
  };

  const subcomponentEditor: SubcomponentEditor = {
    onClickAdd: _onClickAdd,
    onClickEdit: _onClickEdit,
    onClickDelete: _onClickDelete,
    onClickMove: _onClickMove,
    onClickCopy: _onClickCopy
  };

  const { isBasicUser } = useContext(EditorContext)

  return (
    <div
      className={`${classes.elementWrapper} js-controls`}
      draggable={!isBasicUser}
      onDragStart={onDragStart}
    >
      <ErrorBoundary widgetId={widgetName} isEditor>
        <Component
          key={JSON.stringify(componentProps)}
          {...componentProps}
          isRootWidget // Widget is at root of the template (not hosted in another widget)
          editor={subcomponentEditor}
        />
      </ErrorBoundary>
      <div className={classes.dropdown}>
        {
          !isBasicUser &&
          <div className={classes.movers}>
            <div className={classes.mover}>
              <button type="button" style={{ cursor: 'pointer' }} onClick={() => onClickMove('UP')}>
                <i className="material-icons">{showLeftRight ? 'arrow_back' : 'arrow_upward'}</i>
              </button>
            </div>
            <div className={classes.mover}>
              <button type="button" style={{ cursor: 'pointer' }} onClick={() => onClickMove('DOWN')}>
                <i className="material-icons">{showLeftRight ? 'arrow_forward' : 'arrow_downward'}</i>
              </button>
            </div>
          </div>
        }
        <i className="material-icons">settings</i>
        <div className={classes.dropdownContent}>
          <div>
            <button type="button" className={classes.button} onClick={onClickEdit}>
              Edit
            </button>
          </div>
          {
            !isBasicUser &&
            <div>
              <button type="button" className={classes.button} onClick={onClickCopy}>
                Copy
              </button>
            </div>
          }
          {
            !isSubcomponent &&
            <div>
              <button type="button" className={classnames(classes.button, classes.presetButton)} onClick={onClickPresets}>
                <span>Presets</span>
                {
                  isUsingPresetId &&
                  <i className="material-icons">check</i>
                }
              </button>
            </div>
          }
          {
            !isBasicUser &&
            <div>
              <button type="button" className={classes.button} onClick={confirmDelete}>
                Delete
              </button>
            </div>
          }
        </div>
      </div>
      <div className={classes.elementLabel}>
        {widgetName}
      </div>
    </div>
  );
}

const styles = {
  dropdown: {
    cursor: 'pointer',
    position: 'absolute',
    display: 'block',
    top: '0',
    right: '0',
    zIndex: zIndex.SLOT_CONTROL,
    backgroundColor: 'rgba(255,255,255,0.2)',
    'div:hover > &': {
      display: 'flex',
      flexFlow: 'column nowrap'
    },
    '&:hover > $dropdownContent': {
      display: 'block'
    },
    '&> i': {
      fontSize: '35px'
    }
  },
  dropdownContent: {
    display: 'none',
    border: '1px solid rgb(204,204,204)',
    position: 'absolute',
    zIndex: zIndex.SLOT_CONTROL + 1,
    textAlign: 'center',
    top: '32px',
    right: '0'
  },
  button: {
    border: 'none',
    width: '120px',
    backgroundColor: '#fff',
    zIndex: zIndex.SLOT_CONTROL + 2,
    height: '30px',
    textTransform: 'uppercase',
    fontSize: '15px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgb(204,204,204)',
    }
  },
  presetButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&>.material-icons': {
      fontSize: '16px',
      color: 'hsl(145, 34%, 49%)',
      paddingBottom: '2px',
      marginLeft: '2px'
    }
  },
  elementWrapper: {
    position: 'relative',
    width: '100%',
    '&:hover': {
      cursor: 'pointer'
    },
    '&:hover:after': {
      content: '" "',
      position: 'absolute',
      left: '0',
      right: '0',
      top: '0',
      bottom: '0',
      background: 'hsla(0, 0%, 100%, 0.4)',
      borderRadius: '3px',
      border: '1px dashed hsl(0, 0%, 40%)',
      zIndex: '2'
    }
  },
  elementLabel: {
    display: 'none',
    background: 'hsl(0, 0%, 0%)',
    position: 'absolute',
    left: '0',
    top: '-19px',
    zIndex: zIndex.SLOT_CONTROL,
    color: 'hsl(0, 0%, 100%)',
    padding: '3px 20px',
    fontSize: '10px',
    'div:hover > &': {
      display: 'block'
    }
  },
  movers: {
    display: 'none',
    position: 'absolute',
    top: '0',
    right: '0',
    zIndex: zIndex.SLOT_CONTROL,
    'div:hover > &': {
      display: 'flex',
      flexFlow: 'row nowrap'
    },
    cursor: 'pointer'
  },
  mover: {
    backgroundColor: 'none',
    cursor: 'pointer'
  }
};

export default injectSheet(styles)(SlotControls);
